import posthog from "posthog-js";
import { isTelemetryAllowedHost } from "./allowedHosts";

export type TrackedScreen = "/browse" | "/cafe/:slug";

export type AbortExitType = "navigation" | "background" | "pagehide";

export interface Pending {
  screen: TrackedScreen;
  startedAt: number; // performance.now() at nav start (0 for the initial load)
  initial: boolean;
  cacheHitCandidate: boolean;
}

let currentPending: Pending | null = null;
let pendingVersion = 0;

export function getPending(): Pending | null {
  return currentPending;
}

export function getPendingVersion(): number {
  return pendingVersion;
}

export function setPending(p: Pending): void {
  currentPending = p;
  pendingVersion += 1;
}

export function clearPending(): void {
  if (currentPending !== null) {
    currentPending = null;
    pendingVersion += 1;
  }
}

export function markCacheHitCandidate(): void {
  if (currentPending) currentPending.cacheHitCandidate = true;
}

/**
 * Normalize a raw pathname to the closed enum of screens we instrument this
 * phase. Returns null for routes we don't track (keeps event cardinality low
 * and avoids leaking slugs/ids into event properties).
 */
export function normalizeScreen(pathname: string): TrackedScreen | null {
  if (pathname === "/browse") return "/browse";
  if (/^\/cafe\/[^/]+$/.test(pathname)) return "/cafe/:slug";
  return null;
}

function shouldEmit(): boolean {
  return isTelemetryAllowedHost();
}

export function captureCompleted(
  pending: Pending,
  now: number,
  cacheHit: boolean,
): void {
  if (!shouldEmit()) return;
  const duration = Math.max(0, Math.round(now - pending.startedAt));
  try {
    posthog.capture("screen_load_completed", {
      screen: pending.screen,
      duration_ms: duration,
      cache_hit: cacheHit,
      initial: pending.initial,
    });
  } catch { /* ignore */ }
}

export function captureAborted(
  pending: Pending,
  now: number,
  exitType: AbortExitType,
): void {
  if (!shouldEmit()) return;
  const elapsed = Math.max(0, Math.round(now - pending.startedAt));
  try {
    posthog.capture("screen_load_aborted", {
      screen: pending.screen,
      elapsed_ms: elapsed,
      exit_type: exitType,
    });
  } catch { /* ignore */ }
}
